import { teamAbbrev, teamName } from "../../../data/teams";
import { readGameStatus } from "../../../utils/gameStatus";
import TeamLogo from "../TeamLogo/TeamLogo";
import styles from "./Matchup.module.css";

/**
 * Two clubs, balanced either side of a centre column.
 *
 * Crest above abbreviation above nickname, laid out like a broadcast
 * matchup rather than a list row, so a viewer can tell who is playing from
 * across the room without reading a sentence. The centre column is the only
 * part that changes with state: "at" before kickoff, the scoreline during
 * and after. Keeping the clubs pinned either side means a card going live
 * doesn't jump about.
 *
 * This is the shape the schedule card established, extracted so the Home
 * hero and the game page use the same one rather than three that drift.
 *
 * `as`         the heading level this matchup is at on its page: h3 in a
 *              card, h2 in a hero, h1 on the game's own page.
 * `size`       "md" in a card, "lg" where the matchup is the page's headline.
 * `showScore`  normally derived from the status. Force it off where
 *              something below already owns the scoreline, so a live game
 *              doesn't print the same two numbers twice.
 */
const TeamColumn = ({ teamId, score, showScore, leading, logoSize }) => (
  <span className={styles.team}>
    <TeamLogo teamId={teamId} size={logoSize} />
    <span className={styles.abbrev}>{teamAbbrev(teamId)}</span>
    <span className={styles.nickname}>{teamName(teamId)}</span>
    {showScore && (
      <span
        className={`${styles.score} ${
          leading ? styles.leading : ""
        } app-numeric`.trim()}
      >
        {score}
      </span>
    )}
  </span>
);

const Matchup = ({
  game,
  status,
  awayScore,
  homeScore,
  showScore: showScoreOverride,
  as: Tag = "div",
  size = "md",
  className = "",
}) => {
  if (!game) return null;

  const derived = readGameStatus(game, status);
  const showScore = showScoreOverride ?? derived.showScore;

  const away = Number(awayScore ?? game.awayScore ?? 0);
  const home = Number(homeScore ?? game.homeScore ?? 0);

  const large = size === "lg";
  const sizeClass = large ? styles.lg : "";
  const logoSize = large ? "xl" : "lg";

  return (
    <Tag className={`${styles.Matchup} ${sizeClass} ${className}`.trim()}>
      {/* The layout above is visual; this is the sentence version. */}
      <span className="visually-hidden">
        {teamName(game.awayTeamId)} at {teamName(game.homeTeamId)}
        {showScore ? `, ${away} to ${home}` : ""}
      </span>

      <TeamColumn
        teamId={game.awayTeamId}
        score={away}
        showScore={showScore}
        leading={showScore && away > home}
        logoSize={logoSize}
      />

      <span className={styles.divider} aria-hidden="true">
        {showScore ? (
          <span className={styles.dividerDash}>&ndash;</span>
        ) : (
          <span className={styles.dividerAt}>at</span>
        )}
      </span>

      <TeamColumn
        teamId={game.homeTeamId}
        score={home}
        showScore={showScore}
        leading={showScore && home > away}
        logoSize={logoSize}
      />
    </Tag>
  );
};

export default Matchup;
